import React from 'react';
import classNames from 'classnames/bind';
import { format } from 'date-fns';
import { Icon } from 'src/components/Icon';
import { Button } from 'src/components/Button';

import { useSearchQuery } from 'src/hooks/useSearchQuery';
import { useAccountSelect } from 'src/hooks/useAccount';
import { usePaymentSelect, usePaymentAction } from 'src/hooks/usePayment';
import {
  analyticsEvents,
  ESubscriptionReason,
} from 'src/services/analytics/events';

import styles from './SubcribleBanner.module.scss';

import { SubscribleBannerNewPricing } from './SubscribleBannerNewPricing';
import { useRouterPaywallSubscription } from 'src/hooks/route/useRoutePaywallSubscription';
import { useDialogSelect } from 'src/hooks/useDialog';

const sx = classNames.bind(styles);

export interface ISubcribleBannerProps {
  variant?: 'inline';
  text?: string;
  isViewInfo?: boolean;
  mode?: 'chat' | 'navigation' | 'profile';
  reason: `${ESubscriptionReason}`;
}

export const SubcribleBanner = (props: ISubcribleBannerProps) => {
  const {
    reason,
    variant,
    text = 'Get more with',
    isViewInfo = false,
    mode,
  } = props;
  const isUser = useAccountSelect.isUser();
  const isSubscribled = useAccountSelect.isSubscribled();
  const subscription = useAccountSelect.subscription();
  const pricingRules = useAccountSelect.pricingRules();
  const isUpgradeToSexMode = useAccountSelect.isUpgradeToSexMode();
  const routerPaywallSubscription = useRouterPaywallSubscription();
  const bot = useDialogSelect.bot();
  const { setQuery } = useSearchQuery();
  const postCreatePortalSession = usePaymentAction.postCreatePortalSession();
  const getProductsGroups = usePaymentAction.getProductsGroups();
  const getProductsGroupsStatus = usePaymentAction.getProductsGroups.status();
  const productsGroups = usePaymentSelect.productsGroups();

  React.useEffect(() => {
    if (!getProductsGroupsStatus.isFulfilled && productsGroups.subscriptions.length === 0) {
      getProductsGroups({ groups: ['subscriptions'] });
    }
  }, [getProductsGroupsStatus.isFulfilled, productsGroups.subscriptions.length]);

  const handleOpenPaywall = () => {
    routerPaywallSubscription.open({
      botName: bot?.name,
      dialogId: bot?.id?.toString(),
      priority_category: bot?.priority_category,
      reason: reason,
    });
  };

  const handleEditSubscription = () => {
    // [stripe-portal] 1. Фронт → Бекенд: POST /payments/create-portal-session
    // [stripe-portal] 2. Бекенд → Stripe API: Создает portal session
    // [stripe-portal] 3. Stripe → Бекенд: Возвращает portal URL
    // [stripe-portal] 4. Бекенд → Фронт: { url: "https://billing.stripe.com/..." }
    // [stripe-portal] 5. Фронт → aiversations.com: ?portal-session=encoded_url

    const subscriptionProducts = productsGroups.subscriptions?.filter((product: any) => product?.info?.enable) || [];

    if (subscriptionProducts.length === 0) {
      getProductsGroups({ groups: ['subscriptions'] });
      return;
    }

    // Автоматический выбор первого продукта происходит в саге
    postCreatePortalSession({
      reason: reason,
      // price_id не передаем - сага сама выберет первый продукт
    });
  };

  if (isSubscribled) {
    if (!isViewInfo) return <></>;

    if (subscription?.payment_provider === 'stripe') {
      return (
        <>
          {pricingRules?.flow === 'new_pricing' && (
            <SubscribleBannerNewPricing
              reason={reason}
              mode={
                mode ? mode : variant === 'inline' ? 'profile' : 'navigation'
              }
            />
          )}

          <div className={sx('container', variant)}>
            <div className={sx('title')}>
              <span className={sx('text')}>
                Premium {variant === 'inline' ? 'subscription' : ''}{' '}
                {subscription?.end_datetime
                  ? format(new Date(subscription?.end_datetime), 'dd.MM.yyyy')
                  : 'lifetime'}
              </span>
            </div>
            <div className={sx('link')}>
              <Button
                size="sm"
                variant="filled"
                color="secondary"
                onClick={handleEditSubscription}
              >
                Unsubscribe
              </Button>
            </div>
          </div>
        </>
      );
    }

    if (subscription?.payment_provider === 'paypal') {
      return (
        <div className={sx('container', variant)}>
          <div className={sx('title')}>
            <span className={sx('text')}>
              Premium {variant === 'inline' ? 'subscription' : ''}{' '}
            </span>
          </div>
        </div>
      );
    }

    if (subscription?.payment_provider === 'finpayme') {
      return (
        <div className={sx('container', variant)}>
          <div className={sx('title')}>
            <span className={sx('text')}>
              Premium {variant === 'inline' ? 'subscription' : ''}{' '}
              {subscription?.expires_at
                ? `until ${format(new Date(subscription.expires_at), 'dd.MM.yyyy')}`
                : ''}
            </span>
          </div>
          <div className={sx('link')}>
            <Button
              size="sm"
              variant="filled"
              color="secondary"
              onClick={handleOpenPaywall}
            >
              Renew
            </Button>
          </div>
        </div>
      );
    }
  }

  if (pricingRules?.flow === 'new_pricing') {
    return (
      <SubscribleBannerNewPricing
        reason={reason}
        mode={mode ? mode : variant === 'inline' ? 'profile' : 'navigation'}
      />
    );
  }

  return (
    <div className={sx('container', variant)}>
      <div className={sx('title')}>
        <span className={sx('text')}>{text}</span>
        <span className={sx('icon')}>
          <Icon name="Crown" />
        </span>
        <span className={sx('grade')}>Premium</span>
      </div>

      <Button className={sx('button')} onClick={handleOpenPaywall}>
        Subscribe
      </Button>
    </div>
  );
};
